import {
  Component,
  OnInit,
  QueryList,
  ViewChildren,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { SmartFormModule } from 'ngx-smart-form';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AmlService } from '../_services/aml.service';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, of, switchMap, tap } from 'rxjs';
import { ProcessStatusEnum } from '../_models/proces-status.enum';
import { environment } from '../../environments/environment';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  FormFieldList,
  SfToggleFormField,
  SfUtils,
  SmartFormComponent,
} from 'ngx-smart-form';
import { InfoDocument } from '../_models/info-document';
import { CompletedProcessResponse } from '../_models/completed-process-response';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { DialogService } from '../_services/dialog.service';
import { SpidTaskEnum } from '../_models/spid-task.enum';
import { AmlTaskEnum } from '../_models/aml-task.enum';
@Component({
  selector: 'app-issue-sign',
  standalone: true,
  imports: [
    SmartFormModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    CommonModule,
  ],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { floatLabel: 'always', appearance: 'outline' },
    },
  ],
  templateUrl: './issue-sign.component.html',
  styleUrl: './issue-sign.component.scss',
})
export class IssueSignComponent implements OnInit {
  @ViewChildren(SmartFormComponent)
  smartFormComponentList?: QueryList<SmartFormComponent>;

  private amlService = inject(AmlService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private sanitizer = inject(DomSanitizer);
  private dialogService = inject(DialogService);

  form: FormGroup;
  infoDocuments?: InfoDocument[];
  infocertBase = environment.infocertBase;
  preContrattuale: {
    url: SafeResourceUrl;
    text: string;
    signatures: FormFieldList;
  }[] = [];

  constructor() {
    this.form = this.fb.group({
      otp: [
        { value: null, disabled: false },
        [Validators.required, Validators.pattern(/^\d{8}$/)],
      ],
    });
  }

  ngOnInit(): void {
    this.infoDocuments = this.amlService.clausesCustomerSign as any;
    this.preContrattuale =
      this.amlService.clausesCustomerSign?.flatMap((sign) => {
        return {
          url: this.sanitizer.bypassSecurityTrustResourceUrl(
            `${this.infocertBase}${sign.url}`
          ),
          text: sign.id,
          signatures: sign.signatures.map((signature) => [
            new SfToggleFormField({
              formControlName: signature.id,
              label: signature.text,
              validators: [Validators.requiredTrue],
            }),
          ]),
        };
      }) || [];
  }

  ngOnDestroy(): void {
    this.amlService.clausesCustomerSign = undefined;
  }

  private createForm() {
    this.form = this.fb.group({
      otp: [
        { value: null, disabled: false },
        [Validators.required, Validators.pattern(/^\d{8}$/)],
      ],
    });
  }

  resend() {
    this.amlService.resendOtp().subscribe((res) => {
      this.dialogService.onSuccess(
        'Un nuovo otp è stato inviato al numero di telefono del firmatario.'
      );
    });
  }

  confirm() {
    const customerSignId = this.route.snapshot.paramMap.get('issueSignId');
    if (
      this.form.valid &&
      SfUtils.isValid(this.smartFormComponentList?.toArray()) &&
      customerSignId
    ) {
      const documentsAccepted = JSON.stringify(
        this.parseInfoDocuments(this.infoDocuments ?? [])
      );
      const signTask =
        this.amlService.taskRecord[AmlTaskEnum.SIGN] ||
        this.amlService.taskRecord[SpidTaskEnum.SIGN];
      this.amlService
        .completeTask<CompletedProcessResponse>(signTask!, {
          documents: documentsAccepted,
          otp: this.form.controls['otp'].value,
          restart: false,
        })
        .pipe(
          tap((res) => {
            if (res?.enrollment?.documents) {
              this.amlService.signedDocuments = res?.enrollment?.documents;
            }
          }),
          switchMap((res) => {
            if (res?.enrollment.status === ProcessStatusEnum.COMPLETED) {
              this.router.navigate([
                this.amlService.idPratica,
                'process',
                this.amlService.processId,
                'complete',
              ]);
              return of(undefined);
            } else {
              return this.amlService.getActiveTask().pipe(
                tap((res) => {
                  const path = this.amlService.getPath(res);
                  this.router.navigate(path);
                })
              );
            }
          }),
          catchError((err) => {
            return this.amlService.getActiveTask();
          })
        )
        .subscribe();
    }
  }

  parseInfoDocuments(infoDocuments: InfoDocument[]) {
    const parsedArray: any[] = [];
    for (const infoDoc of infoDocuments) {
      const parsedItem = {
        id: infoDoc.id,
        clauses: infoDoc.signatures.map((signature) => ({
          id: signature.id,
          value: true,
        })),
      };

      parsedArray.push(parsedItem);
    }

    return parsedArray;
  }

  get formIsValid() {
    return (
      SfUtils.isValid(this.smartFormComponentList?.toArray()) && this.form.valid
    );
  }
}
